import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';

const ContextMenu = ({ isOpen, position, items, onClose, header }) => {
  const [computedPosition, setComputedPosition] = useState({ x: -9999, y: -9999 });
  const menuRef = useRef(null);

  const requestedPosition = useMemo(() => {
    const x = typeof position?.x === 'number' ? position.x : 0;
    const y = typeof position?.y === 'number' ? position.y : 0;
    return { x, y };
  }, [position?.x, position?.y]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const menuEl = menuRef.current;
    const padding = 6;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const menuRect = menuEl?.getBoundingClientRect();
    const menuWidth = menuRect?.width ?? 200;
    const menuHeight = menuRect?.height ?? 200;

    let posX = requestedPosition.x;
    let posY = requestedPosition.y;

    if (posX + menuWidth > screenWidth) {
      posX = Math.max(padding, screenWidth - menuWidth - padding);
    }
    if (posY + menuHeight > screenHeight) {
      posY = Math.max(padding, screenHeight - menuHeight - padding);
    }

    setComputedPosition({ x: posX, y: posY });
  }, [isOpen, requestedPosition.x, requestedPosition.y]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointer = (event) => {
      if (event.target?.closest('[data-context-menu-root="true"]')) {
        return;
      }
      onClose?.(event);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.(event);
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('contextmenu', handlePointer);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('contextmenu', handlePointer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const menu = (
    <AnimatePresence>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.96, y: -4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -4 }}
        transition={{ duration: 0.12 }}
        data-context-menu-root="true"
        className="fixed z-[100010] min-w-[180px] overflow-hidden rounded-xl border border-white/[0.08] bg-[var(--color-bg-base)] backdrop-blur-md shadow-2xl shadow-black/50"
        style={{ top: computedPosition.y, left: computedPosition.x }}
      >
        {header && (
          <div className="border-b border-white/[0.07]">
            {header}
          </div>
        )}
        <div className="py-1">
          {items
            ?.filter(Boolean)
            .map(({ label, icon: Icon, onClick, danger, disabled }, index) => (
              <button
                key={`${label}-${index}`}
                type="button"
                onClick={() => {
                  if (disabled) return;
                  onClick?.();
                  onClose?.();
                }}
                className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-sm font-medium transition-colors duration-150 ${
                  disabled
                    ? 'cursor-not-allowed text-[#4a6080]'
                    : danger
                    ? 'text-red-400 hover:bg-red-500/10'
                    : 'text-[var(--color-text-secondary)] hover:bg-white/[0.05]'
                }`}
                disabled={disabled}
              >
                {Icon && (
                  <Icon
                    className={`h-4 w-4 ${
                      disabled
                        ? 'text-[#4a6080]'
                        : danger
                        ? 'text-red-400'
                        : 'text-[var(--color-text-muted)]'
                    }`}
                    strokeWidth={2.2}
                  />
                )}
                <span className="flex-1 text-left">{label}</span>
              </button>
            ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(menu, document.body);
};

export default ContextMenu;
